import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calendarService, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import {
    getTrialCalendarFilter,
    getTrialLessonTagName,
    getTrialOptionItems,
    getTrialRequestErrorMessage,
    getTrialSelectedIdItems,
    IAvailableTrialTutor,
    ITrialLesson,
    trialLessonService,
} from "../../../entities/trialLesson";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { MyInput } from "../../../shared/ui/input";
import { Modal } from "../../../shared/ui/modal";
import { MyTextarea } from "../../../shared/ui/myTextarea";
import { formatTutorConversion, formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";
import classes from './trialLessonEdit.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    trialLesson: ITrialLesson;
    onSuccess: () => void;
}

type TLoadingStage = 'tgAccountId' | 'tagId' | 'userId' | null

const getTrialLessonTagId = (trialLesson: ITrialLesson): string => {
    if(typeof trialLesson.tag === 'string'){
        return ''
    }

    return trialLesson.tag?.id || ''
}

export const TrialLessonEdit: FC<IProps> = ({open, setOpen, trialLesson, onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const trialLessonTagId = useMemo(() => getTrialLessonTagId(trialLesson), [trialLesson])
    const trialLessonTagName = useMemo(() => getTrialLessonTagName(trialLesson), [trialLesson])

    const [tgAccountId, setTgAccountId] = useState<string>(trialLesson.tgAccount.id)
    const [selectedTagId, setSelectedTagId] = useState<string>(trialLessonTagId)
    const [userId, setUserId] = useState<string>(trialLesson.tutor.userId)
    const [tag, setTag] = useState<string>(trialLessonTagName)
    const [studentName, setStudentName] = useState<string>(trialLesson.studentName)
    const [studentTg, setStudentTg] = useState<string>(trialLesson.studentTg)
    const [publicComment, setPublicComment] = useState<string>(trialLesson.publicComment)
    const [privateInfo, setPrivateInfo] = useState<string>(trialLesson.privateInfo)
    const [tgAccountOptions, setTgAccountOptions] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const [tagOptions, setTagOptions] = useState<IGetCalendarFilterOptionsResponse['tags']>([])
    const [availableTutors, setAvailableTutors] = useState<IAvailableTrialTutor[]>([])
    const [loadingStage, setLoadingStage] = useState<TLoadingStage>(null)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [error, setError] = useState<string>('')

    const optionsControllerRef = useRef<AbortController | null>(null)
    const tutorsControllerRef = useRef<AbortController | null>(null)

    const tgAccountItems = useMemo(() => (
        getTrialOptionItems(tgAccountOptions, item => item.title || `@${item.username}`)
    ), [tgAccountOptions])

    const tagItems = useMemo(() => (
        getTrialOptionItems(tagOptions, item => item.name)
    ), [tagOptions])

    const tutorItems = useMemo<IItem<string>[]>(() => {
        const items: IItem<string>[] = availableTutors.map(({tutor, conversion, conversionBase, conversionClosedCount, conversionLostCount}) => ({
            id: tutor.userId,
            name: tutor.fullName,
            secondaryText: formatTutorLoad(tutor),
            tertiaryText: formatTutorConversion(conversion, conversionBase, conversionClosedCount, conversionLostCount),
        }))
        const isOriginalSelection = tgAccountId === trialLesson.tgAccount.id && selectedTagId === trialLessonTagId

        if(isOriginalSelection && !items.some(item => item.id === trialLesson.tutor.userId)){
            items.unshift({id: trialLesson.tutor.userId, name: trialLesson.tutor.fullName, secondaryText: formatTutorLoad(trialLesson.tutor)})
        }

        return items
    }, [availableTutors, selectedTagId, tgAccountId, trialLesson, trialLessonTagId])

    const handleError = useCallback((requestError: unknown, fallback: string) => {
        console.log(requestError)

        if(requestError instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else if(requestError instanceof MyError){
            setGlobalMessage({message: requestError.message, type: 'error'})
        }
        else{
            setError(getTrialRequestErrorMessage(requestError, fallback))
        }
    }, [setGlobalMessage, setIsAuth])

    const loadTags = useCallback(async (nextTgAccountId: string) => {
        optionsControllerRef.current?.abort()
        const controller = new AbortController()
        optionsControllerRef.current = controller

        try{
            setLoadingStage('tagId')
            const response = await calendarService.getCalendarFilterOptions(
                {selected: getTrialCalendarFilter(nextTgAccountId, '')},
                controller.signal,
            )

            setTagOptions(response.tags || [])
        }
        catch(requestError){
            if((requestError as Error).name !== 'AbortError') handleError(requestError, 'Ошибка при загрузке тегов')
        }
        finally{
            if(!controller.signal.aborted) setLoadingStage(null)
        }
    }, [handleError])

    const loadAvailableTutors = useCallback(async (
        nextTgAccountId: string,
        nextTagId: string,
        nextTag: string,
    ) => {
        tutorsControllerRef.current?.abort()
        const controller = new AbortController()
        tutorsControllerRef.current = controller

        try{
            setLoadingStage('userId')
            const response = await trialLessonService.findTrialAvailableTutors({
                slot: {
                    startsAt: trialLesson.startsAt,
                    durationSlots: trialLesson.durationSlots,
                },
                tgAccountId: nextTgAccountId,
                tagId: nextTagId,
                tag: nextTag,
                tutorGender: 'TUTOR_GENDER_ANY',
            }, {signal: controller.signal})

            setAvailableTutors(response.tutors)
            setUserId(current => {
                const isAvailable = response.tutors.some(({tutor}) => tutor.userId === current)
                const isOriginalSelection = nextTgAccountId === trialLesson.tgAccount.id &&
                    nextTagId === trialLessonTagId &&
                    current === trialLesson.tutor.userId

                return isAvailable || isOriginalSelection ? current : ''
            })
        }
        catch(requestError){
            if((requestError as Error).name !== 'AbortError'){
                setAvailableTutors([])
                setUserId('')
                handleError(requestError, 'Ошибка при поиске доступных исполнителей')
            }
        }
        finally{
            if(!controller.signal.aborted) setLoadingStage(null)
        }
    }, [
        handleError,
        trialLesson.durationSlots,
        trialLesson.startsAt,
        trialLessonTagId,
        trialLesson.tgAccount.id,
        trialLesson.tutor.userId,
    ])

    useEffect(() => {
        if(!open) return

        let isActual = true

        setTgAccountId(trialLesson.tgAccount.id)
        setSelectedTagId(trialLessonTagId)
        setUserId(trialLesson.tutor.userId)
        setTag(trialLessonTagName)
        setStudentName(trialLesson.studentName)
        setStudentTg(trialLesson.studentTg)
        setPublicComment(trialLesson.publicComment)
        setPrivateInfo(trialLesson.privateInfo)
        setError('')

        const loadInitialOptions = async () => {
            try{
                setLoadingStage('tgAccountId')
                const response = await calendarService.getCalendarFilterOptions(
                    {selected: getTrialCalendarFilter('', '')},
                )

                if(!isActual) return
                setTgAccountOptions(response.tgAccounts)
                await loadTags(trialLesson.tgAccount.id)
            }
            catch(requestError){
                if(isActual) handleError(requestError, 'Ошибка при загрузке данных пробного')
            }
            finally{
                if(isActual) setLoadingStage(null)
            }
        }

        loadInitialOptions()

        return () => {
            isActual = false
            optionsControllerRef.current?.abort()
            tutorsControllerRef.current?.abort()
        }
    }, [handleError, loadAvailableTutors, loadTags, open, trialLesson, trialLessonTagId, trialLessonTagName])

    useEffect(() => {
        if(!open || !tgAccountId || !selectedTagId){
            tutorsControllerRef.current?.abort()
            setAvailableTutors([])
            setUserId('')
            return
        }

        const timeoutId = window.setTimeout(() => {
            loadAvailableTutors(tgAccountId, selectedTagId, tag)
        }, 250)

        return () => window.clearTimeout(timeoutId)
    }, [loadAvailableTutors, open, selectedTagId, tag, tgAccountId])

    const onClose = (value: boolean) => {
        if(!value) setError('')
        setOpen(value)
    }

    const onSelectTgAccount = (item: IItem<string>) => (selected: boolean) => {
        const nextTgAccountId = selected ? item.id : ''

        setError('')
        setTgAccountId(nextTgAccountId)
        setSelectedTagId('')
        setUserId('')
        setTagOptions([])
        setAvailableTutors([])

        if(nextTgAccountId) loadTags(nextTgAccountId)
    }

    const onSelectTag = (item: IItem<string>) => (selected: boolean) => {
        const nextTagId = selected ? item.id : ''

        setError('')
        setSelectedTagId(nextTagId)
        setUserId('')
        setAvailableTutors([])

    }

    const onChangeTag = (value: string) => {
        setError('')
        setTag(value)
        setUserId('')
        setAvailableTutors([])
    }

    const onSelectTutor = (item: IItem<string>) => (selected: boolean) => {
        setError('')
        setUserId(selected ? item.id : '')
    }

    const saveTrialLesson = async () => {
        if(!tgAccountId || !selectedTagId || !userId){
            setError('Выберите рабочий Telegram, тег и исполнителя')
            return
        }
        if(!studentName.trim()){
            setError('Укажите имя ученика')
            return
        }

        try{
            setIsSaving(true)
            await trialLessonService.updateTrialLesson({
                trialLessonId: trialLesson.id,
                tgAccountId,
                userId,
                tagId: selectedTagId,
                tag: tag.trim(),
                studentName: studentName.trim(),
                studentTg: studentTg.trim(),
                publicComment: publicComment.trim(),
                privateInfo: privateInfo.trim(),
            })

            setGlobalMessage({message: 'Пробное занятие обновлено', type: 'ok'})
            onClose(false)
            onSuccess()
        }
        catch(requestError){
            handleError(requestError, 'Ошибка при обновлении пробного занятия')
        }
        finally{
            setIsSaving(false)
        }
    }

    const canViewPrivateInfo = trialLesson.availableActions.includes('TRIAL_LESSON_ACTION_VIEW_PRIVATE_INFO')

    return (
        <Modal open={open} setOpen={onClose}>
            <section className={classes.modal}>
                <section className={classes.header}>
                    <h3>Редактировать пробное занятие</h3>
                    <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                </section>

                <section className={classes.content}>
                    <section className={classes.formGrid}>
                        <section className={classes.field}>
                            <span>Рабочий Telegram</span>
                            <DropDownListSelected
                                items={tgAccountItems}
                                selectedIdItems={getTrialSelectedIdItems(tgAccountId)}
                                onSelected={onSelectTgAccount}
                                isLoading={loadingStage === 'tgAccountId'}
                                search
                            />
                        </section>
                        {Boolean(tgAccountId) && (
                            <section className={classes.field}>
                                <span>Тег</span>
                                <DropDownListSelected
                                    items={tagItems}
                                    selectedIdItems={getTrialSelectedIdItems(selectedTagId)}
                                    onSelected={onSelectTag}
                                    isLoading={loadingStage === 'tagId'}
                                    search
                                />
                            </section>
                        )}
                        {Boolean(selectedTagId) && (
                            <MyInput
                                title="Тег"
                                value={tag}
                                setValue={onChangeTag}
                                setError={setError}
                                isLoading={isSaving}
                            />
                        )}
                        {Boolean(selectedTagId) && (
                            <section className={classes.field}>
                                <span>Исполнитель</span>
                                <DropDownListSelected
                                    items={tutorItems}
                                    selectedIdItems={getTrialSelectedIdItems(userId)}
                                    onSelected={onSelectTutor}
                                    isLoading={loadingStage === 'userId'}
                                    search
                                    emptyText="Нет доступных исполнителей"
                                />
                            </section>
                        )}
                    </section>

                    <section className={classes.formGrid}>
                        <MyInput title="Имя ученика" value={studentName} setValue={setStudentName} setError={setError} isLoading={isSaving} />
                        <MyInput title="Telegram ученика" value={studentTg} setValue={setStudentTg} setError={setError} isLoading={isSaving} />
                    </section>

                    <MyTextarea title="Публичный комментарий" value={publicComment} setValue={setPublicComment} setError={setError} disabled={isSaving} />
                    {canViewPrivateInfo && (
                        <MyTextarea title="Приватная информация" value={privateInfo} setValue={setPrivateInfo} setError={setError} disabled={isSaving} />
                    )}
                </section>

                <section className={classes.actions}>
                    <section className={classes.cancelButton}>
                        <MyButtonSecondary onClick={() => onClose(false)}>Отмена</MyButtonSecondary>
                    </section>
                    <section className={classes.submitButton}>
                        <MyButton isLoading={isSaving} error={error} onClick={saveTrialLesson}>Сохранить</MyButton>
                    </section>
                </section>
            </section>
        </Modal>
    )
}
